import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, CheckBox, FormField, Grid, Heading, List, ResponsiveContext, Select, Stack, Text, TextInput } from "grommet";
import { find } from "lodash";
import * as React from "react";
import Avatar from "react-avatar";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { NavigationButton } from "../components/NavigationButton";
import AutoSizeTextArea from "../components/AutoSizeTextArea";
import SkeletonWrapper from "../components/SkeletonWrapper";
import StyledModal from "../components/StyledModel";
import { Notify, Subscribe } from "../general/NotificationSubscriber";
import { MatchRow } from "../matches/MatchRow";
import { OutcomeColor } from "../matches/OutcomeIcon";
import { TeamName } from "../teams/TeamName";
import { TeamRow } from "../teams/TeamsManagement";
import { getTeams, TeamViewModel } from "../teams/TeamsStore";
import TournamentBracket from "./TournamentBracket";
import TournamentDetails from "./TournamentDetails";
import { TournamentResult } from "./TournamentResult";
import {
    addParticipant,
    AddTournamentParticipantRequest,
    copyTournament,
    CopyTournamentRequest,
    getTournament,
    implementBracket,
    ImplementTournamentBracketRequest,
    PastMatches,
    removeParticipant,
    RemoveTournamentParticipantRequest,
    resetTournament,
    TournamentMatchViewModel,
    TournamentParticipantViewModel,
    TournamentViewModel,
    UpcomingMatches,
    updateParticipant,
    updateTournament,
    UpdateTournamentParticipantRequest,
} from "./TournamentsStore";

import { MatchParticipantOutcomes } from "../matches/MatchParticipantOutcomes";
import { usePermission, useUser } from "../account/AccountStore";
import { MatchParticipantViewModel } from "../matches/MatchesStore";
import TournamentParticipants from "./TournamentParticipants";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]}></FontAwesomeIcon>;
const chain = <FontAwesomeIcon icon={["fad", "chain"]}></FontAwesomeIcon>;
const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;
const message = <FontAwesomeIcon icon={["fad", "message"]}></FontAwesomeIcon>;
const bell = <FontAwesomeIcon icon={["fad", "bell"]}></FontAwesomeIcon>;

export const RenderWinner = ({ tournamentMatch, size }: { tournamentMatch?: TournamentMatchViewModel; size: number }) => {
    const winner = find(tournamentMatch?.match.participants, { teamID: tournamentMatch?.winner }) as MatchParticipantViewModel;

    return (
        <>
            <Box>
                <Text size={size + "rem"} weight="bold" color={OutcomeColor({ outcomeType: "Win" })} textAlign="center">
                    Winner
                </Text>
                <Box align="center" justify="start" gap="small" fill="horizontal">
                    <img src={winner.team.logoUri} width={size * 60 + "px"} alt="" />
                    <TeamName team={winner.team} size={size + "rem"} color={winner.team.color}></TeamName>
                </Box>
            </Box>
        </>
    );
};

const publicOptions = [
    {
        name: "Bracket",
        link: (tournament) => "/public/tournaments/" + tournament.id,
        height: "700px",
    },
    {
        name: "Current Match",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch",
        height: "150px",
    },
    {
        name: "Current Match Team One Name",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/0/name",
        height: "150px",
    },
    {
        name: "Current Match Team One Logo",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/0/logo",
        height: "250px",
    },
    {
        name: "Current Match Team One Name and Logo",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/0/nameWithLogo",
        height: "250px",
    },
    {
        name: "Current Match Team Two Name",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/1/name",
        height: "150px",
    },
    {
        name: "Current Match Team Two Logo",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/1/logo",
        height: "250px",
    },
    {
        name: "Current Match Team Two Name and Logo",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/1/nameWithLogo",
        height: "250px",
    },
    {
        name: "Galaxy - Current Match All Teams and Players",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/players/galaxy",
        height: "250px",
    },
    {
        name: "Current Match Team One Players",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/0/players",
        height: "250px",
    },
    {
        name: "Current Match Team Two Players",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/1/players",
        height: "250px",
    },
    {
        name: "Current Match Team One Player X",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/0/players/X",
        height: "250px",
    },
    {
        name: "Current Match Team Two Player X",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/1/players/X",
        height: "250px",
    },

    {
        name: "Galaxy - Current Match Team One Players",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/0/players/galaxy",
        height: "250px",
    },
    {
        name: "Galaxy - Current Match Team Two Players",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/1/players/galaxy",
        height: "250px",
    },
    {
        name: "Galaxy - Current Match Team One Player X",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/0/players/X/galaxy",
        height: "250px",
    },
    {
        name: "Galaxy - Current Match Team Two Player X",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/currentMatch/1/players/X/galaxy",
        height: "250px",
    },

    {
        name: "Winner",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/winner",
        height: "500px",
    },
    {
        name: "Winner Name",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/winner/name",
        height: "150px",
    },
    {
        name: "Winner Logo",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/winner/logo",
        height: "250px",
    },
    {
        name: "Winner Name and Logo",
        link: (tournament) => "/public/tournaments/" + tournament.id + "/winner/nameWithLogo",
        height: "250px",
    },
];

const TournamentProfile = () => {
    let { id } = useParams<{ id: string }>();
    const user = useUser();
    let { pathname } = useLocation();
    let navigate = useNavigate();

    const [tournament, setTournament] = React.useState(new TournamentViewModel());
    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        if (tournament.id) {
            if (pathname == `/tournaments/${id}`) {
                if (tournament.bracket?.finals?.winner) {
                    navigate(`/tournaments/${id}/result`, { replace: true });
                    // @ts-ignore
                } else if (tournament.matches.length > 0) {
                    navigate(`/tournaments/${id}/matches`, { replace: true });
                    // @ts-ignore
                } else if (tournament.participants.length >= 2) {
                    navigate(`/tournaments/${id}/bracket`, { replace: true });
                } else {
                    navigate(`/tournaments/${id}/teams`, { replace: true });
                }
            }
        }
    }, [pathname, tournament.id]);

    const [rules, setRules] = React.useState(tournament.rules || "");
    const [rulesAsMarkdown, setRulesAsMarkdown] = React.useState(tournament.rulesAsMarkdown || false);

    let [error, setError] = React.useState("");

    React.useEffect(() => {
        loadTournament();
    }, []);

    const size = React.useContext(ResponsiveContext);

    const loadTournament = () => {
        setIsLoading(true);
        getTournament(id).then((tournament) => {
            setUpcomingMatches(UpcomingMatches(tournament));
            setPastMatches(PastMatches(tournament));
            setTournament(tournament);
            setParticipants(tournament.participants || []);
            setIsLoading(false);
            setRules(tournament.rules);
            setRulesAsMarkdown(tournament.rulesAsMarkdown);
            setShowMatchDialog(false);
        });
    };

    const [upcomingMatches, setUpcomingMatches] = React.useState([] as TournamentMatchViewModel[]);
    const [pastMatches, setPastMatches] = React.useState([] as TournamentMatchViewModel[]);

    const [isUpdating, setIsUpdating] = React.useState(false);

    const onChange = (updatedTournament) => {
        setTournament({ ...tournament, ...updatedTournament });
    };

    let update = () => {
        setIsUpdating(true);
        updateTournament({ ...tournament, rules, rulesAsMarkdown } as TournamentViewModel).then(
            (result) => {
                getTournament(id).then((tournament) => setTournament(tournament));
                setIsUpdating(false);
            },
            (error) => {
                setError(error);
                setIsUpdating(false);
            }
        );
    };

    const [teams, setTeams] = React.useState([] as TeamViewModel[]);

    React.useEffect(() => {
        getTeams("").then((teams) => {
            setTeams(teams);
            setTeamSearchResult(teams);
        });
    }, []);

    let [participants, setParticipants] = React.useState([] as TournamentParticipantViewModel[]);

    let [teamSearchResult, setTeamSearchResult] = React.useState([] as Array<TeamViewModel>);

    const search = (filter) => {
        setTeamSearchResult(
            teams.filter((r) => {
                return r.name.toLowerCase().includes(filter.toLowerCase());
            })
        );
    };

    let [teamFilter, setTeamFilter] = React.useState("");
    const [role, setRole] = React.useState("");

    React.useEffect(() => {
        search(teamFilter);
    }, [teamFilter]);

    const startTournament = () => {
        setIsLoading(true);
        implementBracket({ tournamentID: tournament.id } as ImplementTournamentBracketRequest).then((res) => {
            if (res.success) {
                loadTournament();
            } else {
                setError(res.errors.join("\n"));
                setIsLoading(false);
            }
        });
    };

    const [darkMode, setDarkMode] = React.useState(true);
    const [useTeamColor, setUseTeamColor] = React.useState(true);
    const [reverse, setReverse] = React.useState(false);
    const [backgroundColor, setBackgroundColor] = React.useState("transparent");
    const [matchNumber, setMatchNumber] = React.useState(0);

    const copyLink = (link) => () => {
        // @ts-ignore
        navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
            if (result.state == "granted" || result.state == "prompt") {
                navigator.clipboard
                    .writeText(window.location.origin + link + `?darkMode=${darkMode}&useTeamColor=${useTeamColor}&reverse=${reverse}&background=${backgroundColor}&matchNumber=${matchNumber}`)
                    .then(
                        function () {
                            /* clipboard successfully set */
                        },
                        function () {
                            /* clipboard write failed */
                        }
                    );
            }
        });
    };

    const [showMessageDialog, setShowMessageDialog] = React.useState(false);
    const [notifyMessage, setNotifyMessage] = React.useState("");

    const startNotify = () => {
        setShowMessageDialog(true);
    };

    const notify = () => {
        setShowMessageDialog(false);
        Notify({ title: `${tournament.name} update`, body: notifyMessage || "A subscription has been notified", key: `tournament.${tournament.id}` });
    };

    const subscribe = () => {
        Subscribe(`tournament.${tournament.id}`);
    };

    const [selectedMatch, setSelectedMatch] = React.useState({} as TournamentMatchViewModel);
    const [showMatchDialog, setShowMatchDialog] = React.useState(false);
    const startShowMatchDialog = (tournamentMatch: TournamentMatchViewModel) => {
        setShowMatchDialog(true);
        setSelectedMatch(tournamentMatch);
    };

    const [showCopyDialog, setShowCopyDialog] = React.useState(false);
    const startCopy = () => {
        setShowCopyDialog(true);
    };
    const copy = () => {
        copyTournament({
            tournamentID: tournament.id,
        } as CopyTournamentRequest).then((res) => {
            if (res.success) {
                navigate("/tournaments");
            } else {
                setError(res.errors.join("\n"));
            }
        });
    };

    const hasWritePermission = usePermission("sport:tournaments.write");

    const iframeStyle = {
        border: "5px solid #999",
        borderRadius: "5px",
    };

    const [showArchiveModal, setShowArchiveModal] = React.useState(false);

    const startArchive = () => {
        setShowArchiveModal(true);
    };

    const archive = () => {
        updateTournament({ ...tournament, archived: !tournament.archived } as TournamentViewModel).then(() => {
            loadTournament();
            setShowArchiveModal(false);
        });
    };

    const randomizeParticipants = () => {
        setIsLoading(true);
        Promise.all(
            participants.map((p) =>
                updateParticipant(
                    new UpdateTournamentParticipantRequest(
                        p.id,
                        Math.floor(Math.random() * 100),
                        p.aliases.map((a) => a.aliasID)
                    )
                )
            )
        ).then((res) => loadTournament());
    };

    const [publicView, setPublicView] = React.useState(publicOptions[0]);

    const [showResetModal, setShowResetModal] = React.useState(false);

    const startReset = () => {
        setShowResetModal(true);
    };

    const reset = () => {
        if (tournament.id) {
            resetTournament({ tournamentID: tournament.id }).then(() => {
                loadTournament();
                setShowResetModal(false);
            });
        }
    };

    const [showReorderParticipantsModal, setShowReorderParticipantsModal] = React.useState(false);

    const startReorderParticipants = () => {
        setShowReorderParticipantsModal(true);
    };

    const reorderParticipantPrevious = (participant: TournamentParticipantViewModel, index: number) => {
        if (index > 0) {
            const previous = participants[index - 1];
            updateParticipant(
                new UpdateTournamentParticipantRequest(
                    participant.id,
                    previous.seed,
                    participant.aliases.map((a) => a.aliasID)
                )
            ).then(() => loadTournament());
            updateParticipant(
                new UpdateTournamentParticipantRequest(
                    previous.id,
                    participant.seed,
                    previous.aliases.map((a) => a.aliasID)
                )
            ).then(() => loadTournament());
        }
    };

    const reorderParticipantNext = (participant: TournamentParticipantViewModel, index: number) => {
        if (index < participants.length - 1) {
            const next = participants[index + 1];
            updateParticipant(
                new UpdateTournamentParticipantRequest(
                    participant.id,
                    next.seed,
                    participant.aliases.map((a) => a.aliasID)
                )
            ).then(() => loadTournament());
            updateParticipant(
                new UpdateTournamentParticipantRequest(
                    next.id,
                    participant.seed,
                    next.aliases.map((a) => a.aliasID)
                )
            ).then(() => loadTournament());
        }
    };

    return (
        <div>
            <SkeletonWrapper loading={isLoading}>
                <Heading size="small">
                    <Box direction="row" align="center" gap="small">
                        {tournament.name}
                    </Box>
                </Heading>

                <Box direction="row" align="center" justify="center" gap="medium" wrap>
                    {tournament.bracket?.finals?.winner && <NavigationButton href={`/tournaments/${tournament.id}/result`} text={"Result"}></NavigationButton>}
                    <NavigationButton href={`/tournaments/${tournament.id}/matches`} text={"Matches"}></NavigationButton>
                    <NavigationButton href={`/tournaments/${tournament.id}/bracket`} text={"Bracket"}></NavigationButton>
                    <NavigationButton href={`/tournaments/${tournament.id}/teams`} text={"Teams"}></NavigationButton>
                    <NavigationButton href={`/tournaments/${tournament.id}/details`} text={"Details"}></NavigationButton>
                    <NavigationButton href={`/tournaments/${tournament.id}/rules`} text={"Rules"}></NavigationButton>
                    {tournament.isPublic && <NavigationButton href={`/tournaments/${tournament.id}/public`} text={"Public Page"}></NavigationButton>}
                    {error && <NavigationButton href={`/tournaments/${tournament.id}/error`} color={"status-critical"} text={"Errors"}></NavigationButton>}
                </Box>
                <br />
                <Routes>
                    <Route
                        path="result"
                        element={
                            <>
                                <TournamentResult tournament={tournament}></TournamentResult>
                            </>
                        }
                    />
                    <Route
                        path="matches"
                        element={
                            <>
                                <Box gap="small">
                                    {upcomingMatches.length > 0 && (
                                        <>
                                            <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                                Upcoming Matches
                                            </Text>
                                            <Box direction="row" gap="small">
                                                <Button icon={message} label="Subscribe" primary onClick={subscribe}></Button>
                                            </Box>
                                            <Box gap="small">
                                                {upcomingMatches.map((match) => (
                                                    <Stack key={match.tournamentMatchID} anchor="top-left">
                                                        <MatchRow match={match.match}></MatchRow>
                                                        <Text color={match.loserBracket ? "#d65c5c" : ""} size="small" weight="bold">
                                                            {match.tournamentMatchID}
                                                        </Text>
                                                    </Stack>
                                                ))}
                                            </Box>
                                        </>
                                    )}
                                    {pastMatches.length > 0 && (
                                        <>
                                            <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                                Latest Matches
                                            </Text>
                                            <Box gap="small">
                                                {pastMatches.map((match) => (
                                                    <Stack key={match.tournamentMatchID} anchor="top-left">
                                                        <MatchRow match={match.match}></MatchRow>
                                                        <Box pad={"small"}>
                                                            <Text color={match.loserBracket ? "#d65c5c" : ""} size="small" weight="bold">
                                                                {match.tournamentMatchID}
                                                            </Text>
                                                        </Box>
                                                    </Stack>
                                                ))}
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </>
                        }
                    />
                    <Route
                        path="bracket"
                        element={
                            <>
                                <TournamentBracket tournament={tournament} onClick={(tournamentMatch) => startShowMatchDialog(tournamentMatch)}></TournamentBracket>
                                {hasWritePermission && !tournament.locked && (
                                    <Box direction="row" gap="small">
                                        {user.hasRole("Admin") && <Button label="Randomize Participants" onClick={randomizeParticipants}></Button>}
                                        {user.hasRole("Admin") && <Button label="Reorder Participants" onClick={startReorderParticipants}></Button>}
                                        <Button label="Start Tournament" onClick={startTournament}></Button>
                                    </Box>
                                )}
                                <StyledModal isOpen={showReorderParticipantsModal} setIsOpen={setShowReorderParticipantsModal}>
                                    <Box width={"large"}>
                                        <Grid columns={{ size: "50%", count: 2 }} gap={"small"}>
                                            {participants
                                                .sort((a, b) => a.seed - b.seed)
                                                .map((participant, index) => (
                                                    <Box key={index} direction="row" gap="small" justify="between">
                                                        <Box direction="row" align="center">
                                                            <Avatar name={participant.team.name} size="50" round={true} src={participant.team.logoUri}></Avatar>
                                                            <Text>{participant.team.name}</Text>
                                                        </Box>
                                                        <Box direction="row" gap="small">
                                                            <Button icon={<FontAwesomeIcon icon={["fad", "arrow-up"]} />} onClick={() => reorderParticipantPrevious(participant, index)}></Button>
                                                            <Button icon={<FontAwesomeIcon icon={["fad", "arrow-down"]} />} onClick={() => reorderParticipantNext(participant, index)}></Button>
                                                        </Box>
                                                    </Box>
                                                ))}
                                        </Grid>

                                        <Box direction="row" justify="end" pad={"small"} gap="small">
                                            <Button onClick={() => setShowReorderParticipantsModal(false)} label="Close"></Button>
                                        </Box>
                                    </Box>
                                </StyledModal>
                            </>
                        }
                    />
                    <Route path="teams/*" element={<TournamentParticipants participants={participants} tournament={tournament} onChange={loadTournament}></TournamentParticipants>} />
                    <Route
                        path="details"
                        element={
                            <>
                                <TournamentDetails tournament={tournament} isBusy={!hasWritePermission || isUpdating} onChange={onChange}></TournamentDetails>
                                <br />

                                {hasWritePermission && (
                                    <Box gap="small" direction="row">
                                        {tournament.archived && <Button onClick={() => startArchive()} label="Unarchive" color="status-ok"></Button>}
                                        {!tournament.archived && <Button onClick={() => startArchive()} label="Archive" color="status-warning"></Button>}
                                        <Button icon={bell} label="Notify" onClick={startNotify}></Button>
                                        <Button primary onClick={update} disabled={isUpdating} label="Update"></Button>
                                        <Button color="status-warning" onClick={startCopy} disabled={isUpdating} label="Copy"></Button>
                                        {tournament.locked && <Button color="status-critical" onClick={startReset} disabled={isUpdating} label="Reset Tournament"></Button>}
                                    </Box>
                                )}

                                <StyledModal isOpen={showArchiveModal} setIsOpen={setShowArchiveModal}>
                                    <Box width={"medium"}>
                                        {tournament.archived && <Text>Are you sure you want to unarchive this tournament?</Text>}
                                        {!tournament.archived && <Text>Are you sure you want to archive this tournament?</Text>}
                                        <Box direction="row" justify="end" pad={"small"} gap="small">
                                            <Button onClick={() => setShowArchiveModal(false)} label="Cancel"></Button>
                                            {tournament.archived && <Button primary onClick={() => archive()} label="Unarchive" color="status-ok"></Button>}
                                            {!tournament.archived && <Button primary onClick={() => archive()} label="Archive" color="status-warning"></Button>}
                                        </Box>
                                    </Box>
                                </StyledModal>
                            </>
                        }
                    />
                    <Route
                        path="rules"
                        element={
                            <>
                                <Box fill={true}>
                                    <FormField label="Support Markdown">
                                        <CheckBox checked={rulesAsMarkdown} label="Markdown" onChange={(e) => setRulesAsMarkdown(e.target.checked)} disabled={!hasWritePermission || isUpdating} />
                                    </FormField>
                                    <FormField label="Rules">
                                        <AutoSizeTextArea value={rules || ""} onChange={(e) => setRules(e.target.value)} disabled={!hasWritePermission || isUpdating} />
                                    </FormField>
                                </Box>
                            </>
                        }
                    />
                    <Route
                        path="public"
                        element={
                            <Box gap="small">
                                <Button href={"/public/tournaments/" + tournament.id} icon={idBadge} label="View Public Page"></Button>

                                <Box>
                                    <FormField label="Dark Mode">
                                        <CheckBox checked={darkMode} onChange={(e) => setDarkMode(e.target.checked)} />
                                    </FormField>

                                    <FormField label="Use Team Color">
                                        <CheckBox checked={useTeamColor} onChange={(e) => setUseTeamColor(e.target.checked)} />
                                    </FormField>

                                    <FormField label="Reverse">
                                        <CheckBox checked={reverse} onChange={(e) => setReverse(e.target.checked)} />
                                    </FormField>

                                    <FormField label="Next Match Number">
                                        <TextInput type="number" value={matchNumber} onChange={(e) => setMatchNumber(+e.target.value)} />
                                    </FormField>

                                    {/* <FormField label="Reverse">
                                        <ColorPicker style={{ backgroundColor: backgroundColor }}>
                                            <input type="color" value={color} onChange={(e) => selectColor(e.target.value)} />
                                        </ColorPicker>
                                    </FormField> */}
                                </Box>
                                <Select value={publicView} options={publicOptions} labelKey={"name"} onChange={({ option }) => setPublicView(option)}></Select>

                                <Button onClick={copyLink(publicView.link(tournament))} icon={chain} label={`Copy ${publicView.name} Link`}></Button>
                                <iframe
                                    style={iframeStyle}
                                    src={publicView.link(tournament) + `?darkMode=${darkMode}&useTeamColor=${useTeamColor}&reverse=${reverse}&background=${backgroundColor}&matchNumber=${matchNumber}`}
                                    width="100%"
                                    height={publicView.height}
                                ></iframe>

                                {/* <Button onClick={copyLink("/public/tournaments/" + tournament.id)} icon={chain} label="Copy Bracket Link"></Button>
                                <iframe style={iframeStyle} src={"/public/tournaments/" + tournament.id} width="100%" height="700px"></iframe>

                                <Button onClick={copyLink("/public/tournaments/" + tournament.id + "/currentMatch")} icon={chain} label="Copy Current Match Link"></Button>
                                <iframe style={iframeStyle} src={"/public/tournaments/" + tournament.id + "/currentMatch"} width="100%" height="150px"></iframe>

                                <Button onClick={copyLink("/public/tournaments/" + tournament.id + "/currentMatch/0/name")} icon={chain} label="Copy Current Match Team One Name Link"></Button>
                                <iframe style={iframeStyle} src={"/public/tournaments/" + tournament.id + "/currentMatch/0/name"} width="100%" height="150px"></iframe>

                                <Button onClick={copyLink("/public/tournaments/" + tournament.id + "/currentMatch/0/logo")} icon={chain} label="Copy Current Match Team One Logo Link"></Button>
                                <iframe style={iframeStyle} src={"/public/tournaments/" + tournament.id + "/currentMatch/0/logo"} width="100%" height="250px"></iframe>

                                <Button onClick={copyLink("/public/tournaments/" + tournament.id + "/currentMatch/0/nameWithLogo")} icon={chain} label="Copy Current Match Team One Name and Logo Link"></Button>
                                <iframe style={iframeStyle} src={"/public/tournaments/" + tournament.id + "/currentMatch/0/nameWithLogo"} width="100%" height="250px"></iframe>

                                <Button onClick={copyLink("/public/tournaments/" + tournament.id + "/currentMatch/1/name")} icon={chain} label="Copy Current Match Team Two Name Link"></Button>
                                <iframe style={iframeStyle} src={"/public/tournaments/" + tournament.id + "/currentMatch/1/name"} width="100%" height="150px"></iframe>

                                <Button onClick={copyLink("/public/tournaments/" + tournament.id + "/currentMatch/1/logo")} icon={chain} label="Copy Current Match Team Two Logo Link"></Button>
                                <iframe style={iframeStyle} src={"/public/tournaments/" + tournament.id + "/currentMatch/1/logo"} width="100%" height="250px"></iframe>

                                <Button onClick={copyLink("/public/tournaments/" + tournament.id + "/currentMatch/1/nameWithLogo")} icon={chain} label="Copy Current Match Team Two Name and Logo Link"></Button>
                                <iframe style={iframeStyle} src={"/public/tournaments/" + tournament.id + "/currentMatch/1/nameWithLogo"} width="100%" height="250px"></iframe>

                                <Button onClick={copyLink("/public/tournaments/" + tournament.id + "/winner")} icon={chain} label="Copy Winner Link"></Button>
                                <iframe style={iframeStyle} src={"/public/tournaments/" + tournament.id + "/winner"} width="100%" height="500px"></iframe>

                                <Button onClick={copyLink("/public/tournaments/" + tournament.id + "/winner/name")} icon={chain} label="Copy Winner Name Link"></Button>
                                <iframe style={iframeStyle} src={"/public/tournaments/" + tournament.id + "/winner/name"} width="100%" height="150px"></iframe>

                                <Button onClick={copyLink("/public/tournaments/" + tournament.id + "/winner/logo")} icon={chain} label="Copy Winner Logo Link"></Button>
                                <iframe style={iframeStyle} src={"/public/tournaments/" + tournament.id + "/winner/logo"} width="100%" height="250px"></iframe>

                                <Button onClick={copyLink("/public/tournaments/" + tournament.id + "/winner/nameWithLogo")} icon={chain} label="Copy Winner Name and Logo Link"></Button>
                                <iframe style={iframeStyle} src={"/public/tournaments/" + tournament.id + "/winner/nameWithLogo"} width="100%" height="250px"></iframe> */}
                            </Box>
                        }
                    />
                    <Route
                        path="error"
                        element={
                            <>
                                <pre>{JSON.stringify(error, null, 2)}</pre>
                            </>
                        }
                    />
                </Routes>
            </SkeletonWrapper>

            <StyledModal isOpen={showMatchDialog} setIsOpen={setShowMatchDialog}>
                {selectedMatch.roundID && (
                    <Box gap="small">
                        {selectedMatch.match.participants.map((participant, index) => (
                            <TeamRow team={participant.team} key={index}>
                                <Box direction="row" gap="small">
                                    <MatchParticipantOutcomes
                                        disabled={!hasWritePermission}
                                        match={selectedMatch.match}
                                        participant={participant}
                                        handleChange={() => loadTournament()}
                                    ></MatchParticipantOutcomes>
                                </Box>
                            </TeamRow>
                        ))}
                        <Box justify="end" direction="row" gap="small">
                            <Button label="Close" onClick={() => setShowMatchDialog(false)}></Button>
                            <Button label="View Match" primary onClick={() => navigate("/matches/" + selectedMatch.match.id)}></Button>
                        </Box>
                    </Box>
                )}
            </StyledModal>
            <StyledModal isOpen={showMessageDialog} setIsOpen={setShowMessageDialog}>
                <Box width={"medium"} gap="small">
                    <Text>Notify</Text>
                    <TextInput type="text" placeholder="Message" value={notifyMessage} onChange={(e) => setNotifyMessage(e.target.value)}></TextInput>
                    <Box justify="end" direction="row" gap="small">
                        <Button label="Cancel" onClick={() => setShowMessageDialog(false)}></Button>
                        <Button label="Notify" primary onClick={notify}></Button>
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showCopyDialog} setIsOpen={setShowCopyDialog}>
                <Box width={"medium"} gap="small">
                    <Text>Do you want to make a copy of this tournament?</Text>
                    <Box justify="end" direction="row" gap="small">
                        <Button label="Cancel" onClick={() => setShowMessageDialog(false)}></Button>
                        <Button label="Copy" primary onClick={copy}></Button>
                    </Box>
                </Box>
            </StyledModal>

            <StyledModal isOpen={showResetModal} setIsOpen={setShowResetModal}>
                <Box width={"medium"} gap="small">
                    <Text>Rest</Text>
                    <Text>You are about to reset this tournament, all outcomes and match stats will be removed</Text>
                    <Box justify="end" direction="row" gap="small">
                        <Button label="Cancel" onClick={() => setShowResetModal(false)}></Button>
                        <Button label="Reset" primary color="status-critical" onClick={reset}></Button>
                    </Box>
                </Box>
            </StyledModal>
        </div>
    );
};

export default TournamentProfile;
